import React from 'react'
import classNames from 'classnames'
import { Dropdown } from 'react-bootstrap'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useCustomWorkflowLauncher } from '../../../../components/CustomWorkflows/useCustomWorkflowLauncher'
import { workflowMenuLabel } from '../../../../components/CustomWorkflows/workflowMenuLabel'
import { entityTag } from '../../../../components/CustomWorkflows/entityTags'
import { ActionsDropdownItem, ActionsToggle } from '../../Actions/CrossClientActionsDropdown'
import { PortalDropdownMenu } from '../../../Scheduling/shared/PortalDropdownMenu'
import actionsMenuStyles from '../../Actions/CrossClientActionsDropdown.module.scss'
import { type CrossClientActionScope } from '../../Actions/useActionTracking'
import { CrossClientChooseActionDropdown, type CrossClientSingleActionTarget } from './CrossClientChooseActionDropdown'
import { type SendBookingInvitationsTarget } from '../../SendBookingInvitations/sendBookingInvitationsRoute'
import { pluralize } from '../../utils'
import { CCQ_ACTIONS, CCQ_AGENT_PREVIEW_CLIENT, CCQ_AGENT_PREVIEW_CLIENTS, CCQ_AGENTS, formatCount } from './ccqRunCopy'
import { useCcqRun } from './useCcqRun'

interface CcqActionButtonsProps {
  isPrimary: boolean
  isDisabled: boolean
  resolveTargets: () => Promise<CrossClientQueryResult[]>
  count: number
  scope: CrossClientActionScope
  extraItems?: React.ReactNode
  singleClient?: CrossClientSingleActionTarget
  sendBookingInvitationsTarget?: SendBookingInvitationsTarget
}

const buttonClass = (isPrimary: boolean): string =>
  classNames('button-small rounded-3 d-flex align-items-center gap-2 text-nowrap', {
    'zocks-border-0.5 zocks-border-color-gray-300': !isPrimary,
  })

export const CcqActionButtons: React.FC<CcqActionButtonsProps> = ({
  isPrimary,
  isDisabled,
  resolveTargets,
  count,
  scope,
  extraItems,
  singleClient,
  sendBookingInvitationsTarget,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { execution } = useCcqRun()
  const targetLabel = singleClient?.name ?? `${formatCount(count)} ${pluralize(count, 'client')}`
  const launcher = useCustomWorkflowLauncher({
    accountId,
    scope: entityTag('ccq'),
    context: { execution_id: execution.id },
    contextPreviews: {
      client_ids: (
        <div>
          <div className='paragraph-small medium mb-1'>{singleClient ? CCQ_AGENT_PREVIEW_CLIENT : CCQ_AGENT_PREVIEW_CLIENTS}</div>
          <span className='d-inline-flex align-items-center button-small px-2 py-1 rounded-1 zocks-bg-color-gray-200 zocks-text-gray-900'>{targetLabel}</span>
        </div>
      ),
    },
  })
  const showsAgents = launcher.workflows.length > 0
  const isAgentsPrimary = isPrimary && showsAgents
  const isActionsPrimary = isPrimary && !showsAgents

  // The targets are resolved on click, so a bulk run always covers the card as it is now.
  const launch = async (workflowId: string) => {
    try {
      const targets = await resolveTargets()
      if (targets.length === 0) {
        return
      }
      const clientIds = Array.from(new Set(targets.map((result) => result.clientId)))
      launcher.launch(workflowId, { execution_id: execution.id, client_ids: { client_ids: clientIds } })
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className='d-flex align-items-center gap-2 flex-shrink-0'>
      {showsAgents ? (
        <Dropdown>
          <ActionsToggle variant={isAgentsPrimary ? 'primary' : 'header'} className={buttonClass(isAgentsPrimary)} disabled={isDisabled}>
            {CCQ_AGENTS}
            <i className='bi bi-chevron-expand' aria-hidden='true' />
          </ActionsToggle>
          {/* Portaled: an inline menu would be clipped by the card it opens from. */}
          <Dropdown.Menu as={PortalDropdownMenu} popperConfig={{ strategy: 'fixed' }} className={classNames(actionsMenuStyles.menu, 'p-2')}>
            {launcher.workflows.map((workflow) => (
              <ActionsDropdownItem key={workflow.id} icon='bi-stars' label={workflowMenuLabel(workflow)} onClick={() => void launch(workflow.id)} />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      ) : null}
      <CrossClientChooseActionDropdown
        executionId={execution.id}
        count={count}
        scope={scope}
        resolveTargets={resolveTargets}
        label={CCQ_ACTIONS}
        toggleVariant={isActionsPrimary ? 'primary' : 'header'}
        toggleClassName={buttonClass(isActionsPrimary)}
        extraItems={extraItems}
        disabled={isDisabled}
        singleClient={singleClient}
        sendBookingInvitationsTarget={sendBookingInvitationsTarget}
      />
      {launcher.runModal}
    </div>
  )
}
